import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, LogOut, Search, Trash2, Mail, MailCheck, Inbox, Activity, ExternalLink, FileText, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import AdminPostsPanel from "@/components/AdminPostsPanel";
import AdminCasesPanel from "@/components/AdminCasesPanel";
import Seo from "@/components/Seo";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("briefs");

  const loadAll = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/contacts", { params: query ? { q: query } : {} }),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setSelected((prev) => prev ?? c.data[0] ?? null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadAll(q), 300);
    return () => clearTimeout(t);
  }, [q, loadAll]);

  const filtered = contacts;

  const statCards = useMemo(() => ([
    { k: "Total briefs", v: stats?.total_submissions ?? "—", i: Inbox, color: "#00F0FF" },
    { k: "Notifications sent", v: stats?.emails_delivered ?? "—", i: MailCheck, color: "#39FF14" },
    { k: "Auto-replies sent", v: stats?.confirmations_sent ?? "—", i: Mail, color: "#FF00FF" },
    { k: "Last activity", v: stats?.latest_at ? new Date(stats.latest_at).toLocaleString() : "—", i: Activity, color: "#00F0FF" },
  ]), [stats]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Submission deleted");
      setContacts((prev) => prev.filter((c) => c.id !== id));
      setSelected((prev) => (prev?.id === id ? null : prev));
      loadAll(q);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white noise" data-testid="admin-dashboard">
      <Seo title="Admin" path="/admin" description="Spin Labs admin dashboard." noindex />
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF00FF] flex items-center justify-center">
              <Zap className="w-4 h-4 text-black" strokeWidth={3} />
            </div>
            <div className="font-display font-black text-sm">SPIN<span className="text-[#00F0FF]">.</span>LABS</div>
            <span className="ml-3 font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">/ admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-white/60 font-mono-neo uppercase tracking-[0.2em]">{user?.email}</span>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-white/10 hover:border-[#FF00FF]/50 hover:text-[#FF00FF] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 space-y-8">
        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10" data-testid="admin-tabs">
          {[
            { k: "briefs", label: "Briefs", Icon: Inbox },
            { k: "posts", label: "Posts", Icon: FileText },
            { k: "cases", label: "Cases", Icon: Briefcase },
          ].map(({ k, label, Icon }) => {
            const active = tab === k;
            return (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`admin-tab-${k}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${active ? "text-[#00F0FF] border-[#00F0FF]" : "text-white/60 border-transparent hover:text-white"}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            );
          })}
        </div>

        {tab === "briefs" && (<>
        {/* Heading */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-2">Sales Pipeline</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tighter">Project briefs</h1>
          <p className="mt-2 text-sm text-white/60">Every form submission from spinlabs.in lands here in real time.</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
          {statCards.map((s) => (
            <div key={s.k} className="p-6 rounded-2xl border border-white/10 bg-[#0A0A0C] hover:border-[#00F0FF]/40 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-white/50">{s.k}</div>
                <s.i className="w-4 h-4" style={{ color: s.color }} />
              </div>
              <div className="font-display text-2xl lg:text-3xl font-black truncate">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Table + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0A0A0C] overflow-hidden">
            <div className="p-5 border-b border-white/10 flex items-center gap-3">
              <Search className="w-4 h-4 text-white/40" />
              <Input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, email, company, message..."
                className="bg-transparent border-none h-8 p-0 text-sm focus-visible:ring-0 text-white placeholder:text-white/40"
              />
              <span className="font-mono-neo text-xs text-white/40 whitespace-nowrap">{contacts.length} result{contacts.length === 1 ? "" : "s"}</span>
            </div>
            <div className="max-h-[60vh] overflow-auto">
              {loading ? (
                <div className="p-10 text-center text-white/40 font-mono-neo text-xs uppercase tracking-[0.2em]">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-white/40 font-mono-neo text-xs uppercase tracking-[0.2em]">No submissions yet</div>
              ) : (
                filtered.map((c) => {
                  const active = selected?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      data-testid={`admin-contact-row-${c.id}`}
                      onClick={() => setSelected(c)}
                      className={`w-full text-left px-5 py-4 border-b border-white/5 transition-colors ${active ? "bg-[#00F0FF]/5" : "hover:bg-white/5"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-display font-bold truncate">{c.name}</div>
                            {c.email_sent ? (
                              <span title="Email delivered"><MailCheck className="w-3.5 h-3.5 text-[#39FF14]" /></span>
                            ) : (
                              <span title="Email pending"><Mail className="w-3.5 h-3.5 text-white/40" /></span>
                            )}
                          </div>
                          <div className="text-xs text-white/50 truncate mt-0.5">{c.email}{c.company ? ` · ${c.company}` : ""}</div>
                          <div className="text-xs text-white/40 mt-1 line-clamp-1">{c.message}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]">{c.service}</div>
                          <div className="text-[10px] text-white/40 mt-1">{new Date(c.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#0A0A0C] p-5 sm:p-6 lg:sticky lg:top-24 h-fit" data-testid="admin-detail-panel">
            {selected ? (
              <>
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-3">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
                <h2 className="font-display text-2xl font-black tracking-tight">{selected.name}</h2>
                <a href={`mailto:${selected.email}`} className="text-sm text-[#00F0FF] hover:underline inline-flex items-center gap-1">
                  {selected.email} <ExternalLink className="w-3 h-3" />
                </a>

                <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
                  <Field k="Company" v={selected.company || "—"} />
                  <Field k="Service" v={selected.service} />
                  <Field k="Budget" v={selected.budget || "—"} />
                  <Field k="Notification" v={selected.email_sent ? "Delivered" : "Pending"} color={selected.email_sent ? "#39FF14" : "#71717A"} />
                  <Field k="Auto-reply" v={selected.confirmation_sent ? "Delivered" : "Pending"} color={selected.confirmation_sent ? "#39FF14" : "#71717A"} />
                </dl>

                <div className="mt-6">
                  <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Message</div>
                  <div className="p-4 bg-black/40 border border-white/10 rounded-lg whitespace-pre-wrap text-sm text-white/85 leading-relaxed">
                    {selected.message}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a href={`mailto:${selected.email}?subject=Re: Your Spin Labs brief`} className="btn-primary !px-5 !py-2.5 !text-sm">
                    <Mail className="w-4 h-4" /> Reply
                  </a>
                  <button
                    onClick={() => handleDelete(selected.id)}
                    data-testid="admin-delete-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-sm text-white/70 hover:border-red-500/50 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </>
            ) : (
              <div className="py-16 text-center text-white/40 text-sm font-mono-neo uppercase tracking-[0.2em]">
                Select a submission to view
              </div>
            )}
          </div>
        </div>
        </>)}

        {tab === "posts" && <AdminPostsPanel />}
        {tab === "cases" && <AdminCasesPanel />}
      </main>
    </div>
  );
}

function Field({ k, v, color }) {
  return (
    <div>
      <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">{k}</div>
      <div className="font-medium text-sm truncate" style={color ? { color } : undefined}>{v}</div>
    </div>
  );
}
